import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  Users, 
  DollarSign, 
  ChevronLeft, 
  ChevronRight,
  MapPin,
  Check,
  AlertCircle,
  X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { isAuthenticated } from '../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingPage = () => {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedClass, setSelectedClass] = useState(null);
  const [bookingStatus, setBookingStatus] = useState({ show: false, success: false, message: '' });
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    try {
      const startDate = new Date(selectedDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 14);

      const response = await axios.get(
        `${BACKEND_URL}/api/booking/public/schedule?start_date=${startDate.toISOString()}&end_date=${endDate.toISOString()}`
      );
      setSchedule(response.data);
    } catch (err) {
      console.error('Failed to fetch schedule:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    // Check if user is logged in using auth utility
    setIsLoggedIn(isAuthenticated());
    
    fetchSchedule();
  }, [fetchSchedule]);

  const handleBookClass = async (classItem) => {
    // Check if logged in
    if (!isLoggedIn) {
      // Save the class they wanted to book
      sessionStorage.setItem('pendingBooking', classItem.id);
      navigate('/login?redirect=/book');
      return;
    }

    // Check waiver
    if (classItem.requires_waiver) {
      try {
        
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        
        const waiverCheck = await axios.get(
          `${BACKEND_URL}/api/waiver/check/${user.id}`,
          { withCredentials: true }
        );
        
        if (!waiverCheck.data.signed) {
          sessionStorage.setItem('pendingBooking', classItem.id);
          navigate('/waiver');
          return;
        }
      } catch (err) {
        // Waiver check failed - log error but proceed to booking (backend will validate)
        console.warn('Waiver pre-check failed:', err.message || err);
      }
    }

    // Book the class
    try {
      
      const response = await axios.post(
        `${BACKEND_URL}/api/booking/book`,
        { class_instance_id: classItem.id },
        { withCredentials: true }
      );
      
      setBookingStatus({
        show: true,
        success: true,
        message: response.data.message
      });
      
      // Refresh schedule
      fetchSchedule();
      setSelectedClass(null);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to book class';
      
      // Check if waiver needed
      if (errorMsg.includes('waiver')) {
        sessionStorage.setItem('pendingBooking', classItem.id);
        navigate('/waiver');
        return;
      }
      
      setBookingStatus({
        show: true,
        success: false,
        message: errorMsg
      });
    }
  };

  // Group classes by date
  const groupedSchedule = schedule.reduce((acc, item) => {
    const date = new Date(item.start_time).toDateString();
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  const navigateWeek = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setSelectedDate(newDate);
  };

  const formatTime = (isoString) => {
    return new Date(isoString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    }
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  return (
    <div className="booking-page">
      <div className="booking-container">
        {/* Header */}
        <div className="booking-header">
          <h1>Book a Class</h1>
          <p>Find your perfect flight time and reserve your spot.</p>
        </div>

        {/* Status Message */}
        {bookingStatus.show && (
          <div className={`booking-status ${bookingStatus.success ? 'success' : 'error'}`}>
            {bookingStatus.success ? <Check size={20} /> : <AlertCircle size={20} />}
            <span>{bookingStatus.message}</span>
            <button onClick={() => setBookingStatus({ show: false })} className="close-status">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Navigation */}
        <div className="schedule-nav">
          <Button variant="outline" onClick={() => navigateWeek(-1)}>
            <ChevronLeft size={18} />
            Previous
          </Button>
          <span className="date-range">
            {selectedDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
          <Button variant="outline" onClick={() => navigateWeek(1)}>
            Next
            <ChevronRight size={18} />
          </Button>
        </div>

        {/* Schedule */}
        {loading ? (
          <div className="loading-schedule">
            <div className="loading-spinner"></div>
            <p>Loading classes...</p>
          </div>
        ) : Object.keys(groupedSchedule).length === 0 ? (
          <Card className="no-classes">
            <CardContent>
              <Calendar size={48} />
              <h3>No Classes Scheduled</h3>
              <p>Check back soon for new class times!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="schedule-list">
            {Object.entries(groupedSchedule).map(([date, classes]) => (
              <div key={date} className="schedule-day">
                <h3 className="day-header">{formatDate(date)}</h3>
                <div className="day-classes">
                  {classes.map((classItem) => (
                    <Card 
                      key={classItem.id} 
                      className={`class-card ${classItem.is_full ? 'full' : ''} ${selectedClass?.id === classItem.id ? 'selected' : ''}`}
                      onClick={() => !classItem.is_full && setSelectedClass(classItem)}
                    >
                      <div 
                        className="class-color-bar" 
                        style={{ backgroundColor: classItem.color }}
                      />
                      <CardContent className="class-content">
                        <div className="class-main">
                          <div className="class-time">
                            <Clock size={16} />
                            <span>{formatTime(classItem.start_time)}</span>
                            <span className="duration">({classItem.duration_minutes} min)</span>
                          </div>
                          <h4 className="class-name">{classItem.class_name}</h4>
                          <p className="class-description">{classItem.description}</p>
                        </div>
                        
                        <div className="class-meta">
                          <div className="meta-item">
                            <Users size={14} />
                            <span>
                              {classItem.is_full 
                                ? 'Full' 
                                : `${classItem.spots_available} spots left`
                              }
                            </span>
                          </div>
                          <div className="meta-item price">
                            <DollarSign size={14} />
                            <span>{classItem.price}</span>
                          </div>
                          {classItem.instructor && (
                            <div className="meta-item instructor">
                              with {classItem.instructor}
                            </div>
                          )}
                        </div>

                        {selectedClass?.id === classItem.id && !classItem.is_full && (
                          <div className="class-actions">
                            <Button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleBookClass(classItem);
                              }}
                              className="book-btn"
                            >
                              {isLoggedIn ? 'Book This Class' : 'Sign In to Book'}
                            </Button>
                          </div>
                        )}

                        {classItem.is_full && (
                          <div className="full-badge">
                            Class Full - Join Waitlist
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Location Info */}
        <Card className="location-card">
          <CardContent className="location-content">
            <MapPin size={24} />
            <div className="location-info">
              <h4>The Studio inside Anytime Fitness</h4>
              <p>3560 New Centre Dr, Ste 70</p>
              <p>Wilmington, NC 28403</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingPage;
